// @ts-nocheck
/*
 * Port of views/dashboard/ivr/IVR/nodes/ConditionalNode.js (393 lines).
 * reactflow → @xyflow/react; Handle/Position/useReactFlow/
 * useUpdateNodeInternals API identical across libs.
 *
 * Conditions JSON: `conditions` is a list of groups
 * `{ groupId, operator: 'AND' | 'OR', rules: [row] }`. The groups form an
 * if / else-if chain — each gets its own output handle
 * (`${id}_cond_${groupId}`), plus the bottom `${id}_else` handle. Rules
 * inside a group are joined by the group's operator. Each row keeps the
 * original v1 shape (variableId/operatorId/variableType/value + ancestor
 * 'av:' variants). Flat v1 rows are upgraded by normalizeConditionGroups and
 * the old single `${id}_if` edge is moved onto the first group's handle.
 */
import React, {
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Button, Form } from './_rbShim';
import {
  Handle,
  Position,
  useReactFlow,
  useUpdateNodeInternals,
} from '@xyflow/react';
import {
  GitBranch,
  Settings,
  Trash2,
  Plus,
  X,
  Check,
  ArrowRight,
  ArrowDown,
  Info,
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import IvrDrawer from '../Helpers/IvrDrawer';
import { NodesContext } from '../provider/IvrContext';
import { MAX_CONDITIONS } from '@/utilities/constant';
import {
  CONDITION_GROUP_OPERATORS,
  conditionBranchLabel,
  conditionElseHandleId,
  conditionHandleId,
  emptyConditionGroup,
  emptyConditionRule,
  isConditionRuleComplete,
  isCounterFlowVariable,
  legacyConditionIfHandleId,
  normalizeConditionGroups,
} from '../Helpers/utils';

const VARIABLE_TYPES = ['Number', 'Boolean', 'Text'];
const RULE_GRID = '1.3fr 1.1fr 0.9fr 1.3fr 64px';
// Partial number while typing: optional leading "-", digits, one ".".
const NUMBER_INPUT_RE = /^-?\d*\.?\d*$/;

// The "Counter" flow variable comes from the flow_variables master table, so
// it is matched by name/value rather than a fixed id.
const isCounterVariable = (rule: any) =>
  !!rule?.variableId &&
  !String(rule.variableId).startsWith('av:') &&
  isCounterFlowVariable(rule.variableName, rule.variableValue);
const COUNTER_INFO =
  'Counter holds the number of times this condition has been reached to this block.';

const iconBtnStyle = (disabled: boolean, danger = false) => ({
  width: 26,
  height: 26,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 6,
  border: `1px solid ${danger ? '#fecaca' : '#c7d2fe'}`,
  background: danger ? '#fef2f2' : '#eef2ff',
  color: danger ? '#dc2626' : '#4f46e5',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.4 : 1,
  padding: 0,
});

const ConditionalNode = ({ data, id }: any) => {
  const {
    flowVariablesList = [],
    flowOperatorsList = [],
    updateNodeData,
    onRemove,
  } = useContext(NodesContext) as any;
  const { getNodes, getEdges, setEdges, deleteElements } = useReactFlow();

  const { conditions = [] } = data;
  const groups = useMemo(() => normalizeConditionGroups(conditions), [
    conditions,
  ]);
  const handleKey = groups.map((g: any) => g.groupId).join('|');
  const firstGroupId = groups[0]?.groupId;

  // Flows saved before the if / else-if chain wired "If" to `${id}_if`;
  // move that edge onto the first group's handle.
  useEffect(() => {
    if (!firstGroupId) return;
    const legacy = legacyConditionIfHandleId(id);
    if (!getEdges().some((e: any) => e.source === id && e.sourceHandle === legacy))
      return;
    setEdges((eds: any[]) =>
      eds.map((e: any) =>
        e.source === id && e.sourceHandle === legacy
          ? { ...e, sourceHandle: conditionHandleId(id, firstGroupId) }
          : e,
      ),
    );
  }, [id, firstGroupId]);

  // Walk upstream through the flow graph collecting variables earlier nodes
  // made available: DTMF's "make collected digits available" and API Webhook's
  // "play in next node" response params — same logic ApiNode.js uses for its
  // own custom-params picker, so both node types' future variables show up here.
  const getAncestorVariables = () => {
    const vars: any[] = [];
    const seenNames = new Set();
    let currentId = id;

    while (true) {
      const incomingEdge = getEdges().find((e: any) => e.target === currentId);
      if (!incomingEdge) break;
      const parentNode = getNodes().find(
        (n: any) => n.id === incomingEdge.source,
      );
      if (!parentNode) break;

      if (
        Array.isArray(parentNode?.data?.availableVariables) &&
        parentNode.data.availableVariables.length > 0
      ) {
        parentNode.data.availableVariables.forEach((v: any) => {
          if (!seenNames.has(v.name)) {
            seenNames.add(v.name);
            vars.push(v);
          }
        });
      }

      if (
        (parentNode?.type === 'apiNode' || parentNode?.data?.verb === 'api') &&
        Array.isArray(parentNode?.data?.action?.selected_available_params)
      ) {
        parentNode.data.action.selected_available_params.forEach(
          (param: any) => {
            const varName = `{${param}}`;
            if (!seenNames.has(varName)) {
              seenNames.add(varName);
              vars.push({ name: varName, nodeId: parentNode.id, enable: false });
            }
          },
        );
      }

      currentId = parentNode.id;
    }

    return vars;
  };

  // The If/Else handle offsets are fixed in this component, but React Flow only
  // re-measures a node's handle positions when its overall bounding box size
  // changes — it has no way to know two static-looking handles moved without
  // the node itself resizing. Force a re-measure on mount so edges always
  // anchor to where the dots are actually drawn (matters most right after a
  // handle layout change, or when a flow saved under an older layout loads).
  const updateNodeInternals = useUpdateNodeInternals();
  useEffect(() => {
    updateNodeInternals(id);
  }, [id, handleKey, updateNodeInternals]);

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [draftGroups, setDraftGroups] = useState<any[]>([]);
  const [errors, setErrors] = useState<any>({});
  const [ancestorVars, setAncestorVars] = useState<any[]>([]);

  const openModal = () => {
    const seeded = normalizeConditionGroups(
      JSON.parse(JSON.stringify(conditions)),
    );
    setDraftGroups(seeded.length ? seeded : [emptyConditionGroup()]);
    setAncestorVars(getAncestorVariables());
    setErrors({});
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setErrors({});
    setModalIsOpen(false);
  };

  const updateGroup = (gi: number, updater: (group: any) => any) => {
    setDraftGroups((prev) =>
      prev.map((group: any, i: number) => (i === gi ? updater(group) : group)),
    );
  };

  const updateRule = (gi: number, ri: number, updates: any) => {
    updateGroup(gi, (group: any) => ({
      ...group,
      rules: group.rules.map((rule: any, j: number) =>
        j === ri ? { ...rule, ...updates } : rule,
      ),
    }));
  };

  const handleVariableChange = (gi: number, ri: number, variableId: string) => {
    if (variableId.startsWith('av:')) {
      const selected = ancestorVars.find(
        (v: any) => `av:${v.nodeId}:${v.name}` === variableId,
      );
      updateRule(gi, ri, {
        variableId,
        variableName: selected?.name || '',
        variableValue: selected?.name || '',
        operatorId: '',
        operatorLabel: '',
        operatorValue: '',
      });
      return;
    }
    const selected = flowVariablesList.find(
      (v: any) => String(v.id) === variableId,
    );
    updateRule(gi, ri, {
      variableId,
      variableName: selected?.name || '',
      variableValue: selected?.value || '',
      operatorId: '',
      operatorLabel: '',
      operatorValue: '',
    });
  };

  const handleOperatorChange = (gi: number, ri: number, operatorId: string) => {
    const selected = flowOperatorsList.find(
      (o: any) => String(o.id) === operatorId,
    );
    updateRule(gi, ri, {
      operatorId,
      operatorLabel: selected?.name || '',
      operatorValue: selected?.value || '',
    });
  };

  const handleTypeChange = (gi: number, ri: number, variableType: string) => {
    updateRule(gi, ri, { variableType, value: '' });
  };

  const addGroup = () => {
    setDraftGroups((prev) =>
      prev.length >= MAX_CONDITIONS ? prev : [...prev, emptyConditionGroup()],
    );
    setErrors({});
  };

  const removeGroup = (gi: number) => {
    setDraftGroups((prev) => prev.filter((_: any, i: number) => i !== gi));
    setErrors({});
  };

  const addRule = (gi: number, afterRi: number) => {
    updateGroup(gi, (group: any) =>
      group.rules.length >= MAX_CONDITIONS
        ? group
        : {
            ...group,
            rules: [
              ...group.rules.slice(0, afterRi + 1),
              emptyConditionRule(),
              ...group.rules.slice(afterRi + 1),
            ],
          },
    );
    setErrors({});
  };

  const removeRule = (gi: number, ri: number) => {
    updateGroup(gi, (group: any) =>
      group.rules.length <= 1
        ? group
        : { ...group, rules: group.rules.filter((_: any, j: number) => j !== ri) },
    );
    setErrors({});
  };

  const setGroupOperator = (gi: number, operator: string) => {
    updateGroup(gi, (group: any) => ({ ...group, operator }));
  };

  const validateForm = () => {
    if (draftGroups.length === 0) {
      setErrors({ message: 'Add at least one condition' });
      return false;
    }
    if (draftGroups.length > MAX_CONDITIONS) {
      setErrors({
        message: `A conditional node can have at most ${MAX_CONDITIONS} conditions`,
      });
      return false;
    }
    const incomplete: any = {};
    draftGroups.forEach((group: any, gi: number) => {
      group.rules.forEach((rule: any, ri: number) => {
        if (!isConditionRuleComplete(rule)) incomplete[`${gi}-${ri}`] = true;
      });
    });
    if (Object.keys(incomplete).length > 0) {
      setErrors({
        rules: incomplete,
        message: 'Please complete all fields for every condition.',
      });
      return false;
    }
    setErrors({});
    return true;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    // Drop edges hanging off condition groups that were removed.
    const keep = new Set([
      conditionElseHandleId(id),
      ...draftGroups.map((g: any) => conditionHandleId(id, g.groupId)),
      ...(draftGroups[0]?.groupId === firstGroupId
        ? [legacyConditionIfHandleId(id)]
        : []),
    ]);
    const stale = getEdges().filter(
      (e: any) => e.source === id && e.sourceHandle && !keep.has(e.sourceHandle),
    );
    if (stale.length) deleteElements({ edges: stale });

    updateNodeData(id, { ...data, conditions: draftGroups });
    setModalIsOpen(false);
  };

  const HEAD_H = 40;
  const DIV_H = 1;
  const SUMMARY_H = 20;
  const ROW_H = 22;

  const branchTop = (index: number) =>
    HEAD_H + DIV_H + SUMMARY_H + index * ROW_H + ROW_H / 2;

  const groupSummary = (group: any) =>
    group.rules.length > 1
      ? `${group.rules.length} rules (${group.operator})`
      : '1 rule';

  const renderRule = (group: any, gi: number, rule: any, ri: number) => {
    const invalid = !!errors.rules?.[`${gi}-${ri}`];
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: RULE_GRID,
          gap: 10,
          alignItems: 'center',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <Form.Select
          size="sm"
          className="mb-0"
          style={{ flex: 1, minWidth: 0 }}
          value={rule.variableId}
          isInvalid={invalid && !rule.variableId}
          onChange={(e) => handleVariableChange(gi, ri, e.target.value)}
        >
          <option value="">Select variable</option>
          {flowVariablesList.length > 0 && (
            <optgroup label="Flow Variables">
              {flowVariablesList.map((v: any) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
            </optgroup>
          )}
          {ancestorVars.length > 0 && (
            <optgroup label="From Previous Nodes">
              {ancestorVars.map((v: any, j: number) => (
                <option
                  key={`${v.nodeId}-${j}`}
                  value={`av:${v.nodeId}:${v.name}`}
                >
                  {v.name}
                </option>
              ))}
            </optgroup>
          )}
        </Form.Select>
        {isCounterVariable(rule) && (
          <TooltipProvider delayDuration={100}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={COUNTER_INFO}
                  style={{
                    display: 'inline-flex',
                    padding: 0,
                    border: 'none',
                    background: 'none',
                    color: '#6366f1',
                    cursor: 'help',
                    flexShrink: 0,
                  }}
                >
                  <Info size={15} strokeWidth={2} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" style={{ maxWidth: 240 }}>
                {COUNTER_INFO}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        </div>
        <Form.Select
          size="sm"
          value={rule.operatorId}
          disabled={!rule.variableId}
          isInvalid={invalid && !rule.operatorId}
          onChange={(e) => handleOperatorChange(gi, ri, e.target.value)}
        >
          <option value="">Select operator</option>
          {flowOperatorsList.map((o: any) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </Form.Select>
        <Form.Select
          size="sm"
          value={rule.variableType}
          isInvalid={invalid && !rule.variableType}
          onChange={(e) => handleTypeChange(gi, ri, e.target.value)}
        >
          <option value="">Select type</option>
          {VARIABLE_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </Form.Select>
        {rule.variableType === 'Boolean' ? (
          <Form.Select
            size="sm"
            value={rule.value}
            isInvalid={invalid && rule.value === ''}
            onChange={(e) => updateRule(gi, ri, { value: e.target.value })}
          >
            <option value="">Select value</option>
            <option value="true">True</option>
            <option value="false">False</option>
          </Form.Select>
        ) : rule.variableType === 'Number' ? (
          // Text input + pattern filter: a native number input still accepts
          // "+", "e" etc. and then reports an empty value.
          <Form.Control
            size="sm"
            type="text"
            inputMode="decimal"
            value={rule.value}
            isInvalid={invalid && !isConditionRuleComplete(rule)}
            onChange={(e) => {
              if (NUMBER_INPUT_RE.test(e.target.value))
                updateRule(gi, ri, { value: e.target.value });
            }}
          />
        ) : (
          <Form.Control
            size="sm"
            type="text"
            value={rule.value}
            disabled={!rule.variableType}
            isInvalid={invalid && rule.value === ''}
            onChange={(e) => updateRule(gi, ri, { value: e.target.value })}
          />
        )}
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            type="button"
            title={
              group.rules.length >= MAX_CONDITIONS
                ? `Maximum ${MAX_CONDITIONS} rules per condition`
                : 'Add rule'
            }
            disabled={group.rules.length >= MAX_CONDITIONS}
            onClick={() => addRule(gi, ri)}
            style={iconBtnStyle(group.rules.length >= MAX_CONDITIONS)}
          >
            <Plus size={13} strokeWidth={2.5} />
          </button>
          <button
            type="button"
            title="Remove rule"
            disabled={group.rules.length <= 1}
            onClick={() => removeRule(gi, ri)}
            style={iconBtnStyle(group.rules.length <= 1, true)}
          >
            <X size={13} strokeWidth={2.5} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="ivrn" style={{ '--node-color': '#c026d3' } as any}>
      <span
        className="ivrn-icon"
        style={{
          background: '#c026d3',
          color: '#fff',
          top: 'calc(30% - 10px)',
        }}
      >
        <GitBranch size={22} strokeWidth={2} />
      </span>
      <Handle
        type="target"
        position={Position.Left}
        id={id}
        style={{ background: '#c026d3' }}
      />

      {groups.map((group: any, gi: number) => (
        <Handle
          key={group.groupId}
          type="source"
          position={Position.Right}
          id={conditionHandleId(id, group.groupId)}
          style={{
            top: branchTop(gi),
            backgroundColor: '#16a34a',
            width: 8,
            height: 8,
          }}
        />
      ))}
      <Handle
        type="source"
        position={Position.Bottom}
        id={conditionElseHandleId(id)}
        style={{ backgroundColor: '#c026d3', width: 8, height: 8 }}
      />

      <div className="ivrn-head">
        <span className="ivrn-title">Conditional</span>
        {conditions.length > 0 && (
          <span
            className="ivrn-badge"
            style={{ background: '#f5d0fe', color: '#86198f' }}
          >
            {conditions.length}
          </span>
        )}
      </div>
      <div className="ivrn-divider" />
      <div className="ivrn-body" style={{ padding: 0 }}>
        <div
          style={{
            height: SUMMARY_H,
            display: 'flex',
            alignItems: 'center',
            padding: '0 10px 0 96px',
          }}
        >
          {groups.length > 0 ? (
            <span className="ivrn-val" style={{ fontSize: 13 }}>
              {groups.length} condition{groups.length !== 1 ? 's' : ''}
            </span>
          ) : (
            <span className="ivrn-hint">Click ⚙ to add conditions</span>
          )}
        </div>
        {groups.map((group: any, gi: number) => (
          <div
            key={group.groupId}
            title={groupSummary(group)}
            style={{
              height: ROW_H,
              display: 'flex',
              alignItems: 'center',
              padding: '0 10px 0 96px',
              fontSize: 13,
              fontWeight: 600,
              borderTop: '1px solid #f2f4f7',
              color: '#166534',
              whiteSpace: 'nowrap',
            }}
          >
            <Check
              size={11}
              style={{ marginRight: 4, color: '#16a34a', flexShrink: 0 }}
            />
            {conditionBranchLabel(gi)}
            <span
              style={{
                marginLeft: 6,
                fontSize: 11,
                fontWeight: 500,
                color: '#6b7280',
              }}
            >
              {groupSummary(group)}
            </span>
            <ArrowRight
              size={16}
              style={{ marginLeft: 6, color: '#16a34a', flexShrink: 0 }}
            />
          </div>
        ))}
        <div
          style={{
            height: ROW_H,
            display: 'flex',
            alignItems: 'center',
            padding: '0 18px 0 96px',
            fontSize: 13,
            fontWeight: 600,
            borderTop: '1px solid #f2f4f7',
            color: '#86198f',
          }}
        >
          Else
          <ArrowDown
            size={16}
            style={{ marginLeft: 6, color: '#c026d3', flexShrink: 0 }}
          />
        </div>
      </div>

      <IvrDrawer
        show={modalIsOpen}
        onHide={closeModal}
        title="Conditional"
        subtitle="Conditions are checked in order — If, then each Else If — and the first match takes its output. Inside a condition, rules are joined with the AND / OR you pick. If none match, 'Else' fires."
        width="880px"
        footer={
          <>
            <Button variant="secondary" onClick={closeModal}>
              Discard
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Save
            </Button>
          </>
        }
      >
        <div className="ivr-drawer-section">
          <div className="ivr-drawer-section-title">Conditions</div>
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 760 }}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: RULE_GRID,
                  gap: 10,
                  padding: '0 12px 8px',
                  marginRight: 42,
                  fontWeight: 600,
                  fontSize: 13,
                }}
              >
                <span>Variable</span>
                <span>Operator</span>
                <span>Variable Type</span>
                <span>Value</span>
                <span />
              </div>

              {draftGroups.map((group: any, gi: number) => (
                <Fragment key={group.groupId}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: '#166534',
                      margin: gi > 0 ? '14px 0 6px' : '0 0 6px',
                    }}
                  >
                    {conditionBranchLabel(gi)}
                  </div>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 10 }}
                  >
                    <div
                      style={{
                        flex: 1,
                        border: '1px solid hsl(var(--border))',
                        borderRadius: 10,
                        padding: 12,
                      }}
                    >
                      {group.rules.map((rule: any, ri: number) => (
                        <Fragment key={rule.rowId || ri}>
                          {ri > 0 && (
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'center',
                                margin: '8px 0',
                              }}
                            >
                              <Form.Select
                                size="sm"
                                style={{ width: 90 }}
                                value={group.operator}
                                onChange={(e) =>
                                  setGroupOperator(gi, e.target.value)
                                }
                              >
                                {CONDITION_GROUP_OPERATORS.map((op) => (
                                  <option key={op} value={op}>
                                    {op}
                                  </option>
                                ))}
                              </Form.Select>
                            </div>
                          )}
                          {renderRule(group, gi, rule, ri)}
                        </Fragment>
                      ))}
                    </div>
                    <button
                      type="button"
                      className="ivrn-btn ivrn-btn--del"
                      title="Remove condition"
                      onClick={() => removeGroup(gi)}
                    >
                      <Trash2 size={13} strokeWidth={2} />
                    </button>
                  </div>
                </Fragment>
              ))}
            </div>
          </div>

          {errors.message && (
            <Form.Text
              className="text-danger d-block"
              style={{ marginTop: 8 }}
            >
              {errors.message}
            </Form.Text>
          )}

          <div
            style={{
              marginTop: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <Button
              variant="outline-primary"
              size="sm"
              onClick={addGroup}
              disabled={draftGroups.length >= MAX_CONDITIONS}
              title={
                draftGroups.length >= MAX_CONDITIONS
                  ? `Maximum ${MAX_CONDITIONS} conditions allowed`
                  : 'Add condition'
              }
            >
              <Plus size={14} strokeWidth={2} style={{ marginRight: 4 }} />
              Add Condition
            </Button>
            <span
              style={{
                fontSize: 12,
                color:
                  draftGroups.length >= MAX_CONDITIONS ? '#b91c1c' : '#6b7280',
              }}
            >
              {draftGroups.length} / {MAX_CONDITIONS} conditions
            </span>
          </div>

          <div
            style={{
              marginTop: 14,
              fontSize: 12,
              color: '#6b7280',
              background: '#faf5ff',
              border: '1px solid #f3e8ff',
              borderRadius: 8,
              padding: '8px 12px',
            }}
          >
            Each box is one branch — the first is{' '}
            <strong style={{ color: '#16a34a' }}>If</strong>, the rest are{' '}
            <strong style={{ color: '#16a34a' }}>Else If</strong>, checked in
            order. Rules inside a box use the <strong>AND / OR</strong>{' '}
            selected between them. On the canvas every box gets its own output;
            connect each one, plus the{' '}
            <strong style={{ color: '#86198f' }}>Else</strong> output for when
            no condition matches.
          </div>
        </div>
      </IvrDrawer>

      <div className="ivrn-actions">
        <button
          className="ivrn-btn ivrn-btn--cfg"
          title="Configure"
          onClick={openModal}
        >
          <Settings size={14} strokeWidth={2} />
        </button>
        <button
          className="ivrn-btn ivrn-btn--del"
          title="Remove"
          onClick={() => onRemove(id)}
        >
          <Trash2 size={14} strokeWidth={2} />
        </button>
      </div>
    </div>
  );
};

export default ConditionalNode;
